// src/pages/AgentManagement/modals/CreateAgentModal.tsx
// Modal for creating new agent — mục 16, REQUIRE_FIXBUGS.md: tạo đại lý kèm luôn tài khoản đăng
// nhập (username/email/role) trong cùng 1 lần submit, mật khẩu dùng chung 1 giá trị mặc định cố
// định (không cho nhập), đại lý tự đổi sau khi đăng nhập lần đầu.

import React, { useEffect } from "react";
import {
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Spinner,
} from "reactstrap";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import { previewNextAgentCode } from "slices/agentManagement/thunk";
import {
  VN_MOBILE_PHONE_REGEX,
  VN_MOBILE_PHONE_ERROR_MESSAGE,
} from "helpers/phoneValidation";
import { PageActionPermission } from "services/pageActionPermission/PageActionPermission";
import { loadSystemConfigWithCache } from "helpers/systemConfigCache";
import { Department, Role } from "pages/UserManagement/types";
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('agent_management');

// Mật khẩu mặc định cho MỌI tài khoản đại lý mới tạo — chỉ hiển thị cho admin biết để báo lại cho
// đại lý, không phải input (khớp đúng hằng số phía backend CreateAgentCommand.cs).
export const DEFAULT_AGENT_PASSWORD = "123@123aA";

interface CreateAgentModalProps {
  isOpen: boolean;
  toggle: () => void;
  roles: Role[];
  departments: Department[];
  // Mục 19, REQUIRE_FIXBUGS.md — phòng ban của người đang đăng nhập, chỉ dùng để prefill sẵn
  // dropdown "Phòng ban", vẫn chọn được phòng ban khác.
  defaultDepartmentId: number | null;
  onSubmit: (values: any) => void;
}

// Validation schema
const createAgentSchema = Yup.object().shape({
  code: Yup.string()
    .required(st('ma_dai_ly_khong_duoc_bo_error'))
    .matches(/^[A-Z0-9]+$/, st('ma_chi_duoc_chua_chu_hoa_error'))
    .min(2, st('ma_phai_co_it_nhat_2_error'))
    .max(20, st('ma_khong_duoc_qua_20_ky_error')),
  name: Yup.string()
    .required(st('ten_dai_ly_khong_duoc_bo_error'))
    .min(3, st('ten_phai_co_it_nhat_3_error'))
    .max(150, st('ten_khong_duoc_qua_150_ky_error')),
  address: Yup.string()
    .required(st('dia_chi_khong_duoc_bo_trong_error'))
    .max(255, st('dia_chi_khong_duoc_qua_255_error')),
  commissionRate: Yup.number()
    .typeError(st('ty_le_hoa_hong_phai_la_error'))
    .required(st('ty_le_hoa_hong_khong_duoc_error'))
    .min(0, st('ty_le_hoa_hong_khong_duoc_error_2'))
    .max(100, st('ty_le_hoa_hong_khong_duoc_error_3')),
  prizePayoutCommissionRate: Yup.number()
    .typeError(st('ty_le_hoa_hong_tra_thuong_error'))
    .required(st('ty_le_hoa_hong_tra_thuong_error_2'))
    .min(0, st('ty_le_hoa_hong_tra_thuong_error_3'))
    .max(100, st('ty_le_hoa_hong_tra_thuong_error_4')),
  phoneNumber: Yup.string()
    .required(st('so_dien_thoai_khong_duoc_bo_error'))
    .matches(VN_MOBILE_PHONE_REGEX, VN_MOBILE_PHONE_ERROR_MESSAGE),
  email: Yup.string()
    .email(st('dinh_dang_email_khong_hop_le_error'))
    .required(st('email_khong_duoc_bo_trong_error')),
  roleId: Yup.string().required(st('vai_tro_khong_duoc_bo_trong_error')),
});

const CreateAgentModal: React.FC<CreateAgentModalProps> = ({
  isOpen,
  toggle,
  roles,
  departments,
  defaultDepartmentId,
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { previewingCode, previewedCode } = useSelector(
    (state: any) => state.AgentManagement,
  );
  // Hoa hồng trả thưởng mặc định (System Configuration > Hoa hồng mặc định) — chỉ dùng prefill
  // ô "Tỷ lệ hoa hồng" bên dưới, người dùng vẫn sửa được (mục 13.4, REQUIRE_FIXBUGS.md).
  const { config: systemConfig } = useSelector(
    (state: any) => state.SystemConfiguration,
  );

  const initialValues = {
    code: previewedCode || "",
    name: "",
    address: "",
    commissionRate:
      systemConfig?.defaultCommissionRate !== undefined &&
      systemConfig?.defaultCommissionRate !== null
        ? String(systemConfig.defaultCommissionRate)
        : "",
    // Mục 5 (README.md §7) — auto-fill hoa hồng TRẢ THƯỞNG từ System Configuration > Profile Vùng miền.
    prizePayoutCommissionRate:
      systemConfig?.defaultPrizePayoutCommissionRate !== undefined &&
      systemConfig?.defaultPrizePayoutCommissionRate !== null
        ? String(systemConfig.defaultPrizePayoutCommissionRate)
        : "",
    phoneNumber: "",
    email: "",
    roleId: "",
    departmentId: defaultDepartmentId ? String(defaultDepartmentId) : "",
  };

  // Mã đại lý tự động sinh (dạng AGN00001) — lấy trước ngay khi mở form, hiển thị readonly
  // (mục 8.4, READE.md)
  useEffect(() => {
    if (isOpen) {
      dispatch(previewNextAgentCode() as any);
      loadSystemConfigWithCache(dispatch);
    }
  }, [isOpen, dispatch]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        <i className="ri-add-circle-line me-2"></i>
        {st('them_dai_ly_button')}
      </ModalHeader>

      <Formik
        initialValues={initialValues}
        validationSchema={createAgentSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
        }) => (
          <Form>
            <ModalBody>
              <Row>
                <Col lg={6}>
                  <FormGroup>
                    <Label for="code">
                      {st('ma_dai_ly_column')} <span className="text-danger">*</span>
                    </Label>
                    <div className="position-relative">
                      <Input
                        type="text"
                        name="code"
                        id="code"
                        placeholder={previewingCode ? st('dang_sinh_ma_text') : ""}
                        maxLength={20}
                        data-action-code="create-input-code"
                        data-action-name="Mã đại lý (Thêm mới)"
                        value={values.code}
                        disabled
                        className="bg-light"
                      />
                      {previewingCode && (
                        <div className="position-absolute end-0 top-50 translate-middle-y me-2">
                          <Spinner size="sm" color="primary" />
                        </div>
                      )}
                    </div>
                    <small className="text-muted">
                      {st('ma_dai_ly_duoc_he_thong_hint')}
                    </small>
                  </FormGroup>
                </Col>

                <Col lg={6}>
                  <FormGroup>
                    <Label for="name">
                      {st('ten_dai_ly_column')} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="name"
                      id="name"
                      placeholder={st('vd_dai_ly_kim_thanh_placeholder')}
                      maxLength={150}
                      data-action-code="create-input-name"
                      data-action-name="Tên đại lý (Thêm mới)"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={
                        !PageActionPermission.canEnable(
                          location.pathname,
                          "create-input-name",
                        )
                      }
                      invalid={touched.name && !!errors.name}
                    />
                    {touched.name && errors.name && (
                      <FormFeedback>{errors.name}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>

                <Col lg={12}>
                  <FormGroup>
                    <Label for="address">
                      {st('dia_chi_column')} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="address"
                      id="address"
                      placeholder={st('nhap_dia_chi_hoat_dong_placeholder')}
                      maxLength={255}
                      data-action-code="create-input-address"
                      data-action-name="Địa chỉ (Thêm mới)"
                      value={values.address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={
                        !PageActionPermission.canEnable(
                          location.pathname,
                          "create-input-address",
                        )
                      }
                      invalid={touched.address && !!errors.address}
                    />
                    {touched.address && errors.address && (
                      <FormFeedback>{errors.address}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>

                <Col lg={6}>
                  <FormGroup>
                    <Label for="commissionRate">
                      {st('ty_le_hoa_hong_label')} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="number"
                      name="commissionRate"
                      id="commissionRate"
                      placeholder={st('vd_15_placeholder')}
                      min={0}
                      max={100}
                      step={0.01}
                      data-action-code="create-input-commission-rate"
                      data-action-name="Tỷ lệ hoa hồng (Thêm mới)"
                      value={values.commissionRate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={
                        !PageActionPermission.canEnable(
                          location.pathname,
                          "create-input-commission-rate",
                        )
                      }
                      invalid={
                        touched.commissionRate && !!errors.commissionRate
                      }
                    />
                    {touched.commissionRate && errors.commissionRate && (
                      <FormFeedback>{errors.commissionRate}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>

                <Col lg={6}>
                  <FormGroup>
                    <Label for="prizePayoutCommissionRate">
                      {st('ty_le_hoa_hong_tra_thuong_label')} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="number"
                      name="prizePayoutCommissionRate"
                      id="prizePayoutCommissionRate"
                      placeholder={st('vd_5_placeholder')}
                      min={0}
                      max={100}
                      step={0.01}
                      data-action-code="create-input-commission-rate"
                      data-action-name="Tỷ lệ hoa hồng trả thưởng (Thêm mới)"
                      value={values.prizePayoutCommissionRate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={
                        !PageActionPermission.canEnable(
                          location.pathname,
                          "create-input-commission-rate",
                        )
                      }
                      invalid={
                        touched.prizePayoutCommissionRate && !!errors.prizePayoutCommissionRate
                      }
                    />
                    {touched.prizePayoutCommissionRate && errors.prizePayoutCommissionRate && (
                      <FormFeedback>{errors.prizePayoutCommissionRate}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>

                <Col lg={6}>
                  <FormGroup>
                    <Label for="phoneNumber">
                      {st('so_dien_thoai_column')} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="tel"
                      name="phoneNumber"
                      id="phoneNumber"
                      placeholder={st('vd_0901234567_placeholder')}
                      maxLength={10}
                      data-action-code="create-input-phone-number"
                      data-action-name="Số điện thoại (Thêm mới)"
                      value={values.phoneNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={
                        !PageActionPermission.canEnable(
                          location.pathname,
                          "create-input-phone-number",
                        )
                      }
                      invalid={touched.phoneNumber && !!errors.phoneNumber}
                    />
                    {touched.phoneNumber && errors.phoneNumber && (
                      <FormFeedback>{errors.phoneNumber}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <hr className="my-3" />
              <h6 className="mb-3">{st('tai_khoan_dang_nhap_cho_dai_title')}</h6>
              <Alert color="info" className="py-2">
                <i className="ri-information-line me-1"></i>
                {st('ten_dang_nhap_tu_dong_lay_message')}{" "}
                <strong>{DEFAULT_AGENT_PASSWORD}</strong> {st('vui_long_bao_lai_cho_dai_message')}
              </Alert>

              <Row>
                <Col lg={6}>
                  <FormGroup>
                    <Label for="usernamePreview">{st('ten_dang_nhap_column')}</Label>
                    <Input
                      type="text"
                      id="usernamePreview"
                      value={values.code ? values.code.toLowerCase() : ""}
                      placeholder={previewingCode ? st('dang_sinh_ma_text') : ""}
                      disabled
                      className="bg-light"
                    />
                    <small className="text-muted">
                      {st('tu_dong_lay_theo_ma_dai_hint')}
                    </small>
                  </FormGroup>
                </Col>

                <Col lg={6}>
                  <FormGroup>
                    <Label for="email">
                      {st('email_column')} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="email"
                      name="email"
                      id="email"
                      placeholder={st('vd_dl_kimthanh_example_com_placeholder')}
                      data-action-code="create-input-email"
                      data-action-name="Email đại lý (Thêm mới)"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={
                        !PageActionPermission.canEnable(
                          location.pathname,
                          "create-input-email",
                        )
                      }
                      invalid={touched.email && !!errors.email}
                    />
                    {touched.email && errors.email && (
                      <FormFeedback>{errors.email}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>
                <Col lg={6}>
                  <FormGroup>
                    <Label for="departmentId">{st('phong_ban_label')}</Label>
                    <Input
                      type="select"
                      name="departmentId"
                      id="departmentId"
                      data-action-code="create-input-department"
                      data-action-name="Phòng ban đại lý (Thêm mới)"
                      value={values.departmentId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={
                        !PageActionPermission.canEnable(
                          location.pathname,
                          "create-input-department",
                        )
                      }
                    >
                      <option value="">{st('chon_phong_ban_option')}</option>
                      {departments.map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </Input>
                    <small className="text-muted">
                      {st('da_tu_chon_san_phong_ban_hint')}
                    </small>
                  </FormGroup>
                </Col>
                <Col lg={6}>
                  <FormGroup>
                    <Label for="roleId">
                      {st('vai_tro_label')} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="select"
                      name="roleId"
                      id="roleId"
                      data-action-code="create-input-role"
                      data-action-name="Vai trò đại lý (Thêm mới)"
                      value={values.roleId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={
                        !PageActionPermission.canEnable(
                          location.pathname,
                          "create-input-role",
                        )
                      }
                      invalid={touched.roleId && !!errors.roleId}
                    >
                      <option value="">{st('chon_vai_tro_option')}</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </Input>
                    {touched.roleId && errors.roleId && (
                      <FormFeedback>{errors.roleId}</FormFeedback>
                    )}
                    <small className="text-muted">
                      {st('tao_gan_quyen_trang_cho_vai_hint')}
                    </small>
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>

            <ModalFooter>
              {PageActionPermission.canDisplay(
                location.pathname,
                "button-create-submit",
              ) && (
                <Button
                  type="submit"
                  color="primary"
                  data-action-code="button-create-submit"
                  data-action-name="Lưu đại lý mới"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      {st('dang_xu_ly_text')}
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line me-1"></i>
                      {st('tao_moi_text')}
                    </>
                  )}
                </Button>
              )}
              <Button
                type="button"
                color="secondary"
                onClick={toggle}
                disabled={isSubmitting}
              >
                <i className="ri-close-line me-1"></i>
                {st('huy_button')}
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default CreateAgentModal;
